import {FormEvent, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {usePlaceMutations, usePlacePhoto, usePlaces} from '../api/places';
import ErrorBox, {FieldError, isGeneralError} from '../components/feedback/ErrorBox';
import Sheet from '../components/feedback/Sheet';
import {SkeletonList} from '../components/feedback/Skeleton';
import Spinner from '../components/feedback/Spinner';
import Icon from '../components/Icon';
import {confirm} from '../hooks/useConfirm';
import {useFormDraft} from '../hooks/useFormDraft';
import {useReturnTo} from '../hooks/useReturnTo';
import {toast} from '../hooks/useToast';
import {groupPlaces, moveWithin, PLACE_TYPE_ICONS, PLACE_TYPE_LABELS, SPOT_TYPES} from '../lib/placeTree';
import {prepareUpload} from '../lib/resizeImage';
import {Place, PlaceType} from '../types';
import Button from '../components/Button';

// Where things grow: areas (Back garden, Greenhouse, Indoors) holding beds and spots. This is
// the garden mapped out as a list; Phase 4 draws the same rows on an aerial photo.

const tellsLabel = (n: number) => (n === 0 ? 'no tells yet' : n === 1 ? '1 tell' : `${n} tells`);

function AddPlaceForm({parentId, placeholder, onDone}: {parentId: string | null; placeholder: string; onDone?: () => void}) {
    const {create} = usePlaceMutations();
    const [name, setName] = useState('');

    const submit = (event: FormEvent) => {
        event.preventDefault();
        create.mutate(
            {name: name.trim(), parentId},
            {
                onSuccess: () => {
                    setName('');
                    onDone?.();
                }
            }
        );
    };

    return (
        <form onSubmit={submit} className="flex flex-col gap-1">
            <div className="flex gap-2">
                <input
                    className="field min-h-[44px] flex-1"
                    type="text"
                    maxLength={40}
                    placeholder={placeholder}
                    value={name}
                    onChange={e => setName(e.target.value)}
                    disabled={create.isPending}
                    aria-label={placeholder}
                />
                <Button type="submit" variant="secondary" size="sm" busy={create.isPending} busyLabel="Add" disabled={name.trim() === ''}>
                    <Icon name="plus" size={18} /> Add
                </Button>
            </div>
            <FieldError error={create.error} field="name" />
            {isGeneralError(create.error) && <ErrorBox error={create.error} />}
        </form>
    );
}

function PlaceRow({place, onOpen}: {place: Place; onOpen: () => void}) {
    const spot = place.parentId !== null;
    return (
        <button type="button" onClick={onOpen} className={`flex min-h-[56px] w-full items-center gap-3 px-4 text-left ${place.hidden ? 'opacity-60' : ''}`}>
            {place.photoThumbUrl ? (
                <img src={place.photoThumbUrl} alt="" className="h-10 w-10 shrink-0 rounded-[11px] object-cover" />
            ) : (
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] ${spot ? 'bg-kind-grow-bg text-kind-grow-fg' : 'bg-kind-plain-bg text-kind-plain-fg'}`}>
                    <Icon name={PLACE_TYPE_ICONS[place.type]} size={20} />
                </span>
            )}
            <span className="min-w-0 flex-1">
                <span className={`block truncate ${spot ? 'font-semibold' : 'font-display text-lg font-[650]'}`}>{place.name}</span>
                <span className="meta block">
                    {spot ? `${PLACE_TYPE_LABELS[place.type]} · ` : ''}
                    {tellsLabel(place.tellCount)}
                    {place.soil ? ` · ${place.soil}` : ''}
                    {place.hidden ? ' · hidden' : ''}
                </span>
            </span>
            <Icon name="chevron" size={20} className="shrink-0 text-ink-3" />
        </button>
    );
}

/** Name, type, area, photo, soil, size, notes, order, hide and remove; also opened from a place page. */
export function EditPlaceSheet({place, all, onClose}: {place: Place | null; all: Place[]; onClose: () => void}) {
    const {patch, reorder, remove} = usePlaceMutations();
    const photo = usePlacePhoto();
    const form = useFormDraft<{name: string; type: PlaceType; parentId: string | null; soil: string; size: string; notes: string}>(
        `place:${place?.id ?? 'none'}`,
        {name: place?.name ?? '', type: place?.type ?? 'bed', parentId: place?.parentId ?? null, soil: place?.soil ?? '', size: place?.size ?? '', notes: place?.notes ?? ''},
        {label: place?.name ?? '', route: '/places', listed: false}
    );
    const {name, type, parentId, soil, size, notes} = form.values;
    const setName = (v: string) => form.set({name: v});
    const setType = (v: PlaceType) => form.set({type: v});
    // Moving an area into another one makes it a bed; moving a bed out makes it an area.
    const setParentId = (v: string | null) => form.set(v !== null && type === 'area' ? {parentId: v, type: 'bed'} : {parentId: v});

    if (!place) return null;
    const isArea = place.parentId === null;
    const areas = all.filter(p => p.parentId === null && p.id !== place.id && (!p.hidden || p.id === place.parentId));
    const siblings = all.filter(p => p.parentId === place.parentId && !p.hidden).sort((a, b) => a.sortOrder - b.sortOrder).map(p => p.id);
    const index = siblings.indexOf(place.id);
    const hasSpots = all.some(p => p.parentId === place.id);
    const canMove = !hasSpots && areas.length > 0;
    const insideArea = parentId !== null;
    const busy = patch.isPending || reorder.isPending || remove.isPending || photo.upload.isPending || photo.remove.isPending;

    const choosePhoto = async (file: File | undefined) => {
        if (!file) return;
        try {
            const prepared = await prepareUpload(file);
            photo.upload.mutate({id: place.id, prepared}, {onSuccess: () => toast.success('Photo saved.')});
        } catch (error) {
            toast.problem(error instanceof Error ? error.message : 'Could not read that photo.');
        }
    };

    const save = (event: FormEvent) => {
        event.preventDefault();
        const changes: Parameters<typeof patch.mutate>[0] = {id: place.id};
        if (name.trim() !== place.name) changes.name = name.trim();
        if (parentId !== place.parentId) changes.parentId = parentId;
        if (insideArea && type !== place.type) changes.type = type;
        if (soil.trim() !== (place.soil ?? '')) changes.soil = soil.trim() || null;
        if (size.trim() !== (place.size ?? '')) changes.size = size.trim() || null;
        if (notes.trim() !== (place.notes ?? '')) changes.notes = notes.trim() || null;
        if (Object.keys(changes).length === 1) {
            form.clear();
            onClose();
            return;
        }
        patch.mutate(changes, {
            onSuccess: () => {
                form.clear();
                onClose();
            }
        });
    };

    const move = (direction: -1 | 1) => reorder.mutate({parentId: place.parentId, ids: moveWithin(siblings, place.id, direction)});

    const toggleHidden = () => patch.mutate({id: place.id, hidden: !place.hidden}, {onSuccess: () => toast.success(place.hidden ? `${place.name} is back.` : `${place.name} is hidden; its tells are untouched.`), onSettled: onClose});

    const del = async () => {
        const done = await confirm({
            title: `Remove ${place.name}?`,
            body: 'Nothing points at it, so nothing is lost.',
            help: 'A place that tells point at cannot be removed; hide it instead and it keeps its history.',
            confirmLabel: 'Remove',
            busyLabel: 'Removing…',
            destructive: true,
            action: () => remove.mutateAsync(place.id)
        });
        if (done) onClose();
    };

    const cannotDelete = place.tellCount > 0 ? `${tellsLabel(place.tellCount)} use this; hide it instead` : hasSpots ? 'Has places inside it' : null;

    return (
        <Sheet open onClose={onClose} title={isArea ? 'This area' : 'This place'} busy={busy}>
            <form onSubmit={save} className="flex flex-col gap-5">
                <label className="flex flex-col gap-1">
                    <span className="meta">Name</span>
                    <input className="field" type="text" maxLength={40} value={name} onChange={e => setName(e.target.value)} autoFocus required />
                    <FieldError error={patch.error} field="name" />
                </label>

                {insideArea && (
                    <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
                        <legend className="meta mb-1 p-0">What is it?</legend>
                        <div className="flex flex-wrap gap-2">
                            {SPOT_TYPES.map(t => (
                                <button key={t} type="button" className="chip" aria-pressed={type === t} onClick={() => setType(t)}>
                                    <Icon name={PLACE_TYPE_ICONS[t]} size={18} />
                                    {PLACE_TYPE_LABELS[t]}
                                </button>
                            ))}
                        </div>
                    </fieldset>
                )}

                {canMove && (
                    <label className="flex flex-col gap-1">
                        <span className="meta">In which area?</span>
                        <select className="field" value={parentId ?? ''} onChange={e => setParentId(e.target.value || null)}>
                            <option value="">None: it is an area of its own</option>
                            {areas.map(a => (
                                <option key={a.id} value={a.id}>
                                    {a.name}
                                </option>
                            ))}
                        </select>
                        <FieldError error={patch.error} field="parentId" />
                    </label>
                )}

                <div className="flex items-center gap-3">
                    {place.photoThumbUrl ? (
                        <img src={place.photoThumbUrl} alt="" className="h-14 w-14 shrink-0 rounded-[14px] object-cover" />
                    ) : (
                        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[14px] bg-sunken text-ink-3">
                            <Icon name="camera" size={22} />
                        </span>
                    )}
                    <label className="btn btn-secondary btn-sm cursor-pointer">
                        {photo.upload.isPending ? <Spinner size="sm" /> : <Icon name="camera" size={18} />}
                        {place.photoUrl ? 'Change photo' : 'Add a photo'}
                        <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={e => void choosePhoto(e.target.files?.[0])} />
                    </label>
                    {place.photoUrl && (
                        <Button variant="secondary" size="sm" busy={photo.remove.isPending} busyLabel="Removing…" disabled={busy} onClick={() => photo.remove.mutate(place.id)}>
                            Remove
                        </Button>
                    )}
                </div>

                <label className="flex flex-col gap-1">
                    <span className="meta">Soil</span>
                    <input className="field" type="text" maxLength={60} placeholder="Clay, improved with compost each autumn" value={soil} onChange={e => form.set({soil: e.target.value})} />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="meta">Size</span>
                    <input className="field" type="text" maxLength={40} placeholder="2.4 × 1.2 m" value={size} onChange={e => form.set({size: e.target.value})} />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="meta">Notes</span>
                    <textarea className="field min-h-[80px] resize-y py-3" maxLength={500} placeholder="Gets the morning sun only; dries out fast" value={notes} onChange={e => form.set({notes: e.target.value})} />
                </label>

                {isGeneralError(patch.error) && <ErrorBox error={patch.error} />}
                {isGeneralError(remove.error) && <ErrorBox error={remove.error} />}
                {isGeneralError(photo.upload.error) && <ErrorBox error={photo.upload.error} />}

                <div className="flex items-center gap-2">
                    <span className="meta flex-1">Order</span>
                    <button type="button" className="btn btn-secondary btn-sm" aria-label="Move up" disabled={busy || index <= 0} onClick={() => move(-1)}>
                        <Icon name="down" size={18} className="rotate-180" /> Up
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" aria-label="Move down" disabled={busy || index < 0 || index >= siblings.length - 1} onClick={() => move(1)}>
                        <Icon name="down" size={18} /> Down
                    </button>
                </div>

                <div className="flex flex-col gap-2">
                    <Button type="submit" busy={patch.isPending} busyLabel="Saving…" disabled={busy || name.trim() === ''}>
                        Save
                    </Button>
                    <div className="flex gap-2">
                        <Button variant="secondary" className="flex-1" disabled={busy} onClick={toggleHidden}>
                            {place.hidden ? 'Show again' : 'Hide'}
                        </Button>
                        <Button variant="secondary" danger className="flex-1" disabled={busy || cannotDelete !== null} title={cannotDelete ?? undefined} onClick={() => del()}>
                            Remove
                        </Button>
                    </div>
                    {cannotDelete && <p className="meta m-0 text-center">{cannotDelete}.</p>}
                </div>
            </form>
        </Sheet>
    );
}

export default function PlacesPage() {
    const places = usePlaces(true);
    const returnTo = useReturnTo();
    const [showHidden, setShowHidden] = useState(false);
    const [editing, setEditing] = useState<string | null>(null);

    const all = places.data ?? [];
    const groups = useMemo(() => groupPlaces(all).filter(g => showHidden || !g.area.hidden), [all, showHidden]);
    const hiddenCount = all.filter(p => p.hidden).length;
    const editingPlace = all.find(p => p.id === editing) ?? null;

    return (
        <div className="settle-in flex flex-col gap-6">
            {returnTo && (
                <Link to={returnTo} className="btn btn-primary sticky top-2 z-10 shadow-float">
                    <Icon name="back" size={20} /> Back to your tell
                </Link>
            )}
            <header className="pt-2">
                <Link to={returnTo ?? '/more'} className="meta mb-1 inline-flex items-center gap-1 no-underline">
                    <Icon name="back" size={16} /> {returnTo ? 'Your tell' : 'More'}
                </Link>
                <h1 className="display m-0">Places</h1>
                <p className="meta mt-1">Where things grow. Areas hold beds, pots and spots; a tell can point at either.</p>
            </header>

            {places.isPending && <SkeletonList rows={4} />}
            {places.isError && <ErrorBox error={places.error} onRetry={() => void places.refetch()} />}

            {groups.map(({area, spots}) => (
                <section key={area.id} className="flex flex-col gap-3">
                    <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface">
                        <PlaceRow place={area} onOpen={() => setEditing(area.id)} />
                        {spots
                            .filter(s => showHidden || !s.hidden)
                            .map(spot => (
                                <PlaceRow key={spot.id} place={spot} onOpen={() => setEditing(spot.id)} />
                            ))}
                    </div>
                    <AddPlaceForm parentId={area.id} placeholder={`Add a bed or spot in ${area.name}`} />
                </section>
            ))}

            {places.isSuccess && (
                <section className="flex flex-col gap-3">
                    <div className="band-label text-ink-2">New area</div>
                    <AddPlaceForm parentId={null} placeholder="Front garden, Allotment, Jill's place…" />
                </section>
            )}

            {hiddenCount > 0 && (
                <button type="button" className="meta self-start underline-offset-2 hover:underline" onClick={() => setShowHidden(v => !v)}>
                    {showHidden ? 'Hide the hidden ones' : `Show ${hiddenCount} hidden`}
                </button>
            )}

            {editingPlace && <EditPlaceSheet key={editingPlace.id + editingPlace.updatedAt} place={editingPlace} all={all} onClose={() => setEditing(null)} />}
        </div>
    );
}
